import { SaveOutlined } from '@ant-design/icons';
import { Button, Form } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { WorkflowStep } from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';

import { StepDetailsFormFields } from './StepDetailsFormFields';

type Props = {
  /** The step being edited; null hides the modal. */
  step: WorkflowStep | null;
  onCancel: () => void;
  onSubmit: (values: {
    titleEn: string;
    titleFr?: string;
    descriptionEn?: string;
    descriptionFr?: string;
  }) => Promise<void>;
  /** Opens the save-as-custom naming modal for this step. Omitted (no third button) for non-DynamicForm steps. */
  onSaveAsCustom?: () => void;
};

/** Title + description editor. Step type is immutable — to change type, delete + re-add. */
export function EditStepModal({ step, onCancel, onSubmit, onSaveAsCustom }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const open = step !== null;

  useEffect(() => {
    if (step) {
      form.setFieldsValue({
        titleEn: step.titleEn,
        titleFr: step.titleFr,
        descriptionEn: step.descriptionEn,
        descriptionFr: step.descriptionFr,
      });
    } else {
      form.resetFields();
    }
  }, [step, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    await onSubmit({
      titleEn: values.titleEn,
      titleFr: values.titleFr ?? '',
      descriptionEn: values.descriptionEn ?? '',
      descriptionFr: values.descriptionFr ?? '',
    });
  };

  return (
    <BmoModal
      open={open}
      onCancel={onCancel}
      title={t('workflow.steps.editModalTitle')}
      width={560}
      footer={[
        ...(onSaveAsCustom
          ? [
              <Button key="saveAsCustom" icon={<SaveOutlined />} onClick={onSaveAsCustom}>
                {t('workflow.steps.saveAsCustomStep')}
              </Button>,
            ]
          : []),
        <Button key="cancel" onClick={onCancel}>
          {t('common.cancel')}
        </Button>,
        <Button key="save" type="primary" onClick={handleOk}>
          {t('common.save')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <StepDetailsFormFields />
      </Form>
    </BmoModal>
  );
}
